import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductWidget } from '@vestico/model';

import { GlobalsContext } from '../CommonAppContext';

export const useProductName = (product: ProductWidget | undefined | null) => {
  const { stylesConfig } = useContext(GlobalsContext);
  const { i18n } = useTranslation();
  const { languages } = i18n;
  const productName = useMemo(() => {
    if (!product) {
      return '';
    }
    const { name, localizedNames } = product;

    if (languages && localizedNames) {
      for (const language of languages) {
        const localizedName = localizedNames[language];
        if (localizedName && localizedName.length > 0) {
          return localizedName;
        }

        // fallback trying to find matching languages if the product language is more specific than the app language
        // e.g. only translated into 'en', but product has 'en-GB'
        const matchingLanguages = Object.keys(localizedNames).filter((languageKey) => languageKey.indexOf(language) === 0);
        for (const matchingLanguage of matchingLanguages) {
          const localizedName = localizedNames[matchingLanguage];
          if (localizedName && localizedName.length > 0) {
            return localizedName;
          }
        }
      }
    }
    return name;
  }, [product, languages]);

  // Run the product name filter regex if necessary
  const { productNameFilterRegex } = stylesConfig.stylesConfig;
  const finalProductName = useMemo(() => {
    if (!productNameFilterRegex || productNameFilterRegex.length === 0) {
      return productName;
    }

    try {
      const regex = new RegExp(productNameFilterRegex, 'i');
      const result = regex.exec(productName)?.[0];
      if (result) {
        return result;
      }
    } catch (error) {
      console.error(error);
    }
    return productName;
  }, [productName, productNameFilterRegex]);

  return finalProductName;
};
